import { Injectable, OnDestroy } from '@angular/core';
import { from, Subject, Subscription, timer } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Store } from '@ngxs/store';

import {
  FreedomPayTransactionRecord,
  SqliteService
} from '../sqlite/sqlite.service';
import { FreedomPayService } from './freedompay.service';
import { APP_STATE_TOKEN } from 'src/app/store/app/app.state.model';

export interface ReplayConfig {
  maxRetries: number;
  retryDelays: number[]; // in milliseconds
}

@Injectable({
  providedIn: 'root'
})
export class ReplayEngineService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private isProcessing = false;
  private processingStartTime?: number;
  private pollingSubscription?: Subscription;
  private isPollingStarted = false;
  private readonly POLLING_INTERVAL = 120000; // 2 minutes in milliseconds
  private readonly MAX_PROCESSING_TIME = 600000; // 10 minutes max processing time
  private readonly DEFAULT_CONFIG: ReplayConfig = {
    maxRetries: 5,
    retryDelays: [60000, 300000, 60000 * 10, 60000 * 20, 60000 * 30] // 1 minute, 5 minutes, 10 minutes, 20 minutes, 30 minutes
  };

  constructor(
    private store: Store,
    private freedomPayService: FreedomPayService,
    private sqliteService: SqliteService
  ) {}

  ngOnDestroy(): void {
    console.log('ReplayEngine: Shutting down service');
    this.stop();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Start periodic polling for pending transactions
   * Should be called explicitly when the app is ready
   */
  start(): void {
    if (this.isPollingStarted) {
      console.log('ReplayEngine: Polling already started, skipping');
      return;
    }

    // Check if SQLite is available before starting
    if (!this.sqliteService.isAvailable()) {
      console.log(
        'ReplayEngine: SQLite not available, offline transaction replay disabled'
      );
      return;
    }

    console.log(
      `ReplayEngine: Starting periodic polling (every ${
        this.POLLING_INTERVAL / 1000
      }s)`
    );
    this.isPollingStarted = true;
    this.startPeriodicPolling();
  }

  /**
   * Stop periodic polling
   */
  stop(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
      this.isPollingStarted = false;
      console.log('ReplayEngine: Polling stopped');
    }
  }

  private startPeriodicPolling(): void {
    // Use timer(0, interval) to emit immediately, then every 2 minutes
    // This ensures pending transactions are checked right after login
    this.pollingSubscription = timer(0, this.POLLING_INTERVAL)
      .pipe(
        filter(() => {
          // Only poll if network is connected
          const isConnected =
            this.store.selectSnapshot(APP_STATE_TOKEN).networkConnected;
          if (!isConnected) {
            console.log('ReplayEngine: Skipping poll - network disconnected');
          }
          return isConnected;
        }),
        filter(() => {
          // Skip if already processing, but check for timeout to prevent permanent lock
          if (this.isProcessing) {
            const processingDuration = this.processingStartTime
              ? Date.now() - this.processingStartTime
              : 0;

            if (processingDuration > this.MAX_PROCESSING_TIME) {
              console.warn(
                `ReplayEngine: Processing exceeded timeout (${processingDuration}ms), resetting lock`
              );
              this.isProcessing = false;
              this.processingStartTime = undefined;
              return true;
            }

            console.log(
              'ReplayEngine: Skipping poll - replay already in progress'
            );
            return false;
          }
          return true;
        }),
        tap(() => console.log('ReplayEngine: Periodic poll triggered')),
        switchMap(() =>
          // Trigger replay - playOfflineRequests handles pending transaction check internally
          from(this.playOfflineRequests(this.DEFAULT_CONFIG))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => console.log('ReplayEngine: Polling cycle completed'),
        error: (error) =>
          console.error('ReplayEngine: Error in polling cycle', error)
      });
  }

  async playOfflineRequests(
    config: ReplayConfig = this.DEFAULT_CONFIG
  ): Promise<void> {
    if (this.isProcessing) {
      console.log('ReplayEngine: Replay already in progress, skipping');
      return;
    }

    this.isProcessing = true;
    this.processingStartTime = Date.now();
    console.log('ReplayEngine: Starting offline transaction replay');

    try {
      const pendingTransactions =
        await this.sqliteService.getAllPendingTransactions();

      if (pendingTransactions.length === 0) {
        console.log('ReplayEngine: No pending transactions to replay');
        return;
      }

      console.log(
        `ReplayEngine: Found ${pendingTransactions.length} pending transactions`
      );

      for (const transaction of pendingTransactions) {
        await this.processTransaction(transaction, config);
      }

      console.log('ReplayEngine: All transactions processed');
    } catch (error) {
      console.error('ReplayEngine: Error processing offline requests', error);
    } finally {
      this.isProcessing = false;
      this.processingStartTime = undefined;
    }
  }

  private async processTransaction(
    transaction: FreedomPayTransactionRecord,
    config: ReplayConfig
  ): Promise<void> {
    const { request_id, terminal_id, retry_count } = transaction;

    console.log(
      `ReplayEngine: Processing transaction ${request_id} (retry ${retry_count}/${config.maxRetries})`
    );

    // Check if max retries exceeded
    if (retry_count >= config.maxRetries) {
      console.error(
        `ReplayEngine: Transaction ${request_id} exceeded max retries, marking as failed`
      );
      await this.sqliteService.updateTransactionStatus(request_id, 'failed');
      return;
    }

    // Apply delay based on retry count
    const delay =
      config.retryDelays[retry_count] ||
      config.retryDelays[config.retryDelays.length - 1];
    if (delay > 0) {
      console.log(`ReplayEngine: Waiting ${delay}ms before replay attempt`);
      await this.sleep(delay);
    }

    try {
      // Increment retry count
      await this.sqliteService.incrementRetryCount(request_id);

      // Attempt replay by querying terminal status
      const response = await this.freedomPayService
        .processReplayStatus(request_id, terminal_id)
        .toPromise();

      if (this.freedomPayService.isReplayStatusSuccessful(response)) {
        // Update SQLite status to 'success' and keep the record for history
        await this.sqliteService.updateTransactionStatus(request_id, 'success');
        console.log(
          `✓ ReplayEngine: Replay successful for PosSyncId: ${request_id}`
        );
        console.log(
          `ReplayEngine: Transaction ${request_id} marked as success in local storage`
        );
      } else {
        const errorMessage = this.freedomPayService.getErrorMessage(response);
        console.error(
          `✗ ReplayEngine: Replay failed for PosSyncId: ${request_id}`,
          errorMessage
        );

        // Will retry on next network restore or next cycle
        console.log(`ReplayEngine: Transaction ${request_id} will be retried`);
      }
    } catch (error) {
      console.error(
        `ReplayEngine: Error replaying transaction ${request_id}`,
        error
      );
      // Transaction remains in pending state, will retry later
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Manual trigger for testing purposes
   */
  async triggerReplay(): Promise<void> {
    console.log('ReplayEngine: Manual replay triggered');
    await this.playOfflineRequests(this.DEFAULT_CONFIG);
  }

  /**
   * Get current status of pending transactions
   */
  async getPendingTransactionsStatus(): Promise<{
    count: number;
    transactions: FreedomPayTransactionRecord[];
  }> {
    const transactions = await this.sqliteService.getAllPendingTransactions();
    return {
      count: transactions.length,
      transactions
    };
  }
}
